/*
  GEMDRAW — Campaign countdown

  Turns configured campaign dates into a live timer, and handles every state the
  campaign can actually be in: no dates set, not open yet, running, in its last
  day, and closed.

  Two rules shape the whole file:

  - It never invents urgency. With no dates configured it shows the merchant's
    neutral message and no timer at all. When a campaign closes the timer stops
    at zero and never runs negative.
  - It never guesses a timezone. The merchant picks the campaign's timezone and
    the wall-clock time they entered is resolved against it, so a draw set for
    9pm Pacific is 9pm Pacific for a visitor anywhere, across daylight saving.

  PREVIEW MODE is the one deliberate exception, and it is built so it cannot be
  mistaken for a deadline:

  - it only ever engages when there is no real end date, so a verified date
    always wins and preview can never shadow one;
  - it runs on a repeating window derived from the epoch rather than a fixed
    date, so it visibly loops — a looping timer is self-evidently not a deadline;
  - it publishes data-countdown-mode="preview" on the element, so the state is
    inspectable from the DOM, in tests and in the editor;
  - the accessible message says it is a preview rather than reading as urgency.

  Without JavaScript the markup already shows the deadline and the campaign
  state as plain text. This only upgrades that into a ticking timer.
*/

(function () {
  'use strict';

  const SECOND = 1000;
  const MINUTE = 60 * SECOND;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;

  /*
    How far the given timezone sits from UTC at that instant. Derived from
    Intl rather than a fixed offset table, so daylight saving is handled by the
    platform instead of guessed here.
  */
  function offsetAt(timestamp, timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });

      const parts = {};
      formatter.formatToParts(new Date(timestamp)).forEach(function (part) {
        if (part.type !== 'literal') parts[part.type] = part.value;
      });

      const asUtc = Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour) % 24,
        Number(parts.minute),
        Number(parts.second)
      );

      return asUtc - timestamp;
    } catch (error) {
      return 0;
    }
  }

  /*
    Resolves "2026-08-14 21:00 in America/Los_Angeles" to a real instant.
    Two passes settle the case where the naive guess lands on the other side of
    a daylight saving boundary from the true answer.
  */
  function toInstant(value, timeZone) {
    if (!value) return null;

    const match = String(value)
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?/);
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hour = match[4] === undefined ? 0 : Number(match[4]);
    const minute = match[5] === undefined ? 0 : Number(match[5]);

    if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return null;

    const naive = Date.UTC(year, month - 1, day, hour, minute);
    if (!timeZone) return naive;

    let instant = naive - offsetAt(naive, timeZone);
    instant = naive - offsetAt(instant, timeZone);
    return instant;
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  class GemdrawCountdown extends HTMLElement {
    connectedCallback() {
      this.timezone = this.dataset.timezone || undefined;
      this.startAt = toInstant(this.dataset.start, this.timezone);
      this.endAt = toInstant(this.dataset.end, this.timezone);

      this.cells = this.querySelector('[data-gd-countdown-cells]');
      this.stateNode = this.querySelector('[data-gd-countdown-state]');
      this.progress = this.querySelector('[data-gd-progress]');
      this.daysCell = this.querySelector('[data-gd-unit="days"]');

      this.values = {
        days: this.querySelector('[data-gd-value="days"]'),
        hours: this.querySelector('[data-gd-value="hours"]'),
        minutes: this.querySelector('[data-gd-value="minutes"]'),
        seconds: this.querySelector('[data-gd-value="seconds"]'),
      };

      /*
        Preview mode. Reached only with no real end date on the element, which is
        what makes a verified date an automatic override rather than a priority
        that has to be checked.

        The window repeats off the epoch, so it always shows a plausible time
        remaining and openly starts over when it reaches zero. That loop is the
        point: a deadline does not come back, so nothing downstream can read this
        as one.
      */
      this.previewHours = parseFloat(this.dataset.previewHours) || 0;
      this.isPreview = this.endAt === null && this.previewHours > 0;

      if (this.isPreview) {
        this.setAttribute('data-countdown-mode', 'preview');
        this.previewPeriod = this.previewHours * HOUR;
        /*
          A full window from this moment, not the next boundary of a window
          measured off the epoch. Epoch boundaries land wherever they land, so the
          preview frequently opened on eleven minutes remaining and read as a
          campaign about to expire — useless for reviewing the design. Starting
          fresh always shows a healthy figure, and it still loops.
        */
        this.endAt = Date.now() + this.previewPeriod;
      } else if (this.endAt !== null) {
        this.setAttribute('data-countdown-mode', 'real');
      }

      /* No usable end date and no preview means no timer. The configured message
         stands on its own and nothing counts down to a date that does not
         exist.

         The cells are hidden explicitly rather than relying on the hidden
         attribute the markup ships with, because the theme editor re-runs this on
         a node whose cells a previous run may already have revealed. */
      if (this.endAt === null) {
        if (this.cells) this.cells.hidden = true;
        this.setState('undated');
        return;
      }

      this.tick();
      this.timer = window.setInterval(this.tick.bind(this), SECOND);
    }

    disconnectedCallback() {
      if (this.timer) window.clearInterval(this.timer);
    }

    setState(state) {
      if (this.state === state) return;
      this.state = state;
      this.setAttribute('data-state', state);

      /* Only the state change is announced. A timer that spoke every second
         would make the page unusable with a screen reader. */
      if (this.stateNode) {
        const message = this.dataset['state' + state.charAt(0).toUpperCase() + state.slice(1)];
        if (message !== undefined) this.stateNode.textContent = message;
      }
    }

    /* The next window, once the current one runs out. */
    nextPreviewEnd(now) {
      return now + this.previewPeriod;
    }

    /*
      Writes a digit and flags it if it actually changed, so the stylesheet can
      run one short vertical slide on the cells that moved. Comparing first is
      what keeps the other three cells still while the seconds tick — animating
      all four every second is how a timer starts to look like a reel.
    */
    setValue(node, text) {
      if (!node || node.textContent === text) return;
      node.textContent = text;
      node.classList.remove('is-ticking');
      /* Reading offsetWidth restarts the animation; without it a class that is
         removed and re-added inside one frame never replays. */
      void node.offsetWidth;
      node.classList.add('is-ticking');
    }

    render(remaining) {
      if (!this.cells) return;

      const days = Math.floor(remaining / DAY);
      const hours = Math.floor((remaining % DAY) / HOUR);
      const minutes = Math.floor((remaining % HOUR) / MINUTE);
      const seconds = Math.floor((remaining % MINUTE) / SECOND);

      this.setValue(this.values.days, pad(days));
      this.setValue(this.values.hours, pad(hours));
      this.setValue(this.values.minutes, pad(minutes));
      this.setValue(this.values.seconds, pad(seconds));

      /* Inside the last day the days cell is dropped rather than sitting at 00,
         and the progress bar switches to its solid treatment. */
      const finalDay = remaining < DAY;
      if (this.daysCell) this.daysCell.hidden = finalDay;
      this.classList.toggle('gd-countdown-block--final-day', finalDay);

      this.cells.hidden = false;
    }

    updateProgress(now) {
      if (!this.progress || this.startAt === null || this.endAt === null) return;

      const span = this.endAt - this.startAt;
      if (span <= 0) return;

      /* Revealed only once there is a real span to represent, so an empty bar
         never sits under a campaign that has no dates. */
      this.progress.hidden = false;

      const ratio = Math.min(1, Math.max(0, (now - this.startAt) / span));
      this.progress.style.setProperty('--gd-progress-value', ratio.toFixed(4));
      this.progress.setAttribute('aria-valuenow', Math.round(ratio * 100));
    }

    tick() {
      const now = Date.now();

      if (now >= this.endAt) {
        /* A preview window rolls into the next one instead of closing. Nothing
           real has ended, so reporting a closed campaign would be a lie in the
           opposite direction. */
        if (this.isPreview) {
          this.endAt = this.nextPreviewEnd(now);
          this.setState('preview');
          this.render(this.endAt - now);
          return;
        }

        this.render(0);
        this.updateProgress(this.endAt);
        this.setState('closed');
        if (this.cells) this.cells.hidden = true;
        if (this.timer) window.clearInterval(this.timer);
        return;
      }

      if (this.isPreview) {
        this.render(this.endAt - now);
        this.setState('preview');
        return;
      }

      if (this.startAt !== null && now < this.startAt) {
        this.render(this.startAt - now);
        this.setState('upcoming');
        return;
      }

      this.render(this.endAt - now);
      this.updateProgress(now);
      this.setState(this.endAt - now < DAY ? 'ending' : 'live');
    }
  }

  if (!customElements.get('gd-countdown')) {
    customElements.define('gd-countdown', GemdrawCountdown);
  }
})();
